<template>
  <RouterLink
    v-if="to"
    :to="to"
    class="sidebar__menu"
    :active-class="''"
    :class="{
      'router-link-active': isActive,
      'sidebar__menu--minimized': minimized,
    }"
  >
    <Icon
      v-if="icon"
      :name="icon"
      class="sidebar__menu__icon"
    />
    <span
      v-if="!minimized"
      class="sidebar__menu__label"
    >
      <slot />
    </span>
    <Icon
      v-if="iconRight"
      :name="iconRight"
      class="sidebar__menu__icon"
    />
  </RouterLink>
  <a
    v-else-if="href"
    class="sidebar__menu"
    :class="{ 'sidebar__menu--minimized': minimized }"
    :href="href"
    :target="target"
  >
    <Icon
      v-if="icon"
      :name="icon"
      class="sidebar__menu__icon"
    />
    <span
      v-if="!minimized"
      class="sidebar__menu__label"
    >
      <slot />
    </span>
    <Icon
      v-if="iconRight"
      :name="iconRight"
      class="sidebar__menu__icon"
    />
  </a>
  <button
    v-else
    class="sidebar__menu"
    :class="{ 'sidebar__menu--minimized': minimized }"
  >
    <Icon
      v-if="icon"
      :name="icon"
      class="sidebar__menu__icon"
    />
    <span
      v-if="!minimized"
      class="sidebar__menu__label"
    >
      <slot />
    </span>
    <Icon
      v-if="iconRight"
      :name="iconRight"
      class="sidebar__menu__icon"
    />
  </button>
</template>

<script lang="ts" setup>
import {
  RouterLink,
  useLink,
  useRoute,
  type RouteLocationAsPathGeneric,
  type RouteLocationAsRelativeGeneric,
} from 'vue-router'

import Icon from '@/uiKit/Icon/Icon.vue'
import type { IconNames } from '@/uiKit/Icon/icons'
import { computed } from 'vue'

type RouteTo = RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric | string | undefined

const props = defineProps<{
  to?: RouteTo
  loading?: boolean
  minimized?: boolean
  icon?: IconNames
  iconRight?: IconNames
  href?: string
  target?: '_blank' | '_parent' | '_self' | '_top'
}>()

const { href: routeHref } = useLink({ to: computed(() => props.to || '') })

const route = useRoute()

/**
 * Custom active route logic for the sidebar item,
 * which uses a mix of classic vue-router@3 active route by path behavior
 * with an extra piece of logic to handle report routes and the settings dialog
 */
const isActive = computed(() => {
  // The settings dialog opens up by appending a 'settings' query param
  // to the url. If the param is present, the sidebar item should be active.
  if (routeHref.value.includes('settings=')) {
    return route.fullPath.includes('settings=')
  }

  // We have /reports/datasets (which the sidebar item links to)
  // as well as /reports/users. The sidebar item should be active
  // on both routes.
  if (routeHref.value.startsWith('/reports')) {
    return route.fullPath.startsWith('/reports')
  }

  // We have /rules/library (which the sidebar item links to)
  // as well as /rules/testing. The sidebar item should be active
  // on both routes.
  if (routeHref.value.startsWith('/rules')) {
    return route.fullPath.startsWith('/rules')
  }

  // Default vue-router@3 behavior. If the path starts with the route, it's active
  return route.fullPath.startsWith(routeHref.value)
})
</script>

<style lang="scss" scoped>
.sidebar__menu {
  display: flex;
  height: 36px;
  gap: 11px;
  align-items: center;
  padding: 0 10px;
  width: 100%;
  cursor: pointer;

  &--minimized {
    justify-content: center;
    padding: 0;
  }

  @include typography(md-1, inter, 500);
  color: var(--color-text-subtle);
  background: transparent;
  letter-spacing: -0.3px;
  white-space: nowrap;

  border-radius: 8px;
  transition: all 0.2s;

  &.router-link-active {
    background: var(--color-background-selected);
    color: var(--color-text-selected);
    .sidebar__menu__icon {
      color: var(--color-icon-primary);
    }
  }

  &__icon {
    font-size: 20px;
    color: var(--color-icon-subtle);
  }

  &:disabled {
    color: var(--color-text-default);
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    color: var(--color-text-default);
    background-color: var(--color-background-transparent-hovered);
    .sidebar__menu__icon {
      color: var(--color-icon-default);
    }
  }
}
</style>
